/** A BCP-47 language tag such as `en-GB` or `ar`. */
export type Locale = string;

/** The product context that decides wording: who the records are about. */
export type UseCase = 'clinical' | 'survey' | 'education';

export const USE_CASES: readonly UseCase[] = [
  'clinical',
  'survey',
  'education',
];

export const DEFAULT_LOCALE: Locale = 'en-GB';
export const DEFAULT_USE_CASE: UseCase = 'clinical';

/** A flat map of message key to ICU MessageFormat source. */
export type Catalogue = Readonly<Record<string, string>>;

/**
 * Catalogues keyed by the file name they came from, without `.json`:
 * `en-GB`, `en-GB.clinical`, `en-US`, `ar`, `ar.survey`.
 */
export type CatalogueSet = Readonly<Record<string, Catalogue>>;

/** Arguments for a message: plain values, numbers, dates. */
export type MessageArgs = Readonly<Record<string, unknown>>;

/** How text crosses the wire: a key and its arguments, never a rendered string. */
export interface MessageRef {
  readonly key: string;
  readonly params?: MessageArgs | undefined;
}

export type TextDirection = 'ltr' | 'rtl';

export function isUseCase(value: unknown): value is UseCase {
  return USE_CASES.some((useCase) => useCase === value);
}

/** Parses decoded JSON into message arguments: any object, never an array or primitive. */
export function parseMessageArgs(value: unknown): MessageArgs {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError('message arguments are a JSON object');
  }
  return Object.fromEntries(Object.entries(value));
}

/**
 * Parses a decoded JSON document into a catalogue: an object whose every
 * value is a string. Anything else is a malformed file, named by `source`.
 */
export function parseCatalogue(value: unknown, source: string): Catalogue {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(
      `${source}: a catalogue is a JSON object of key to message`,
    );
  }
  const entries: Array<[string, string]> = [];
  for (const [key, message] of Object.entries(value)) {
    if (typeof message !== 'string')
      throw new TypeError(`${source}:${key}: message must be a string`);
    entries.push([key, message]);
  }
  return Object.fromEntries(entries);
}
